export const AGENT_COLLECTIONS_SELECTION_KEY = 'collectionsAgentSelection';

export type AgentCollectionsSelection = {
  projectId?: number;
  projectName?: string;
  collectionId?: number;
  requestId?: number;
  environmentId?: number;
};

function parsePositiveId(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value) && value > 0) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return undefined;
}

export function writeAgentCollectionsSelection(selection: AgentCollectionsSelection): void {
  try {
    const cleaned: AgentCollectionsSelection = {};
    const projectId = parsePositiveId(selection.projectId);
    const collectionId = parsePositiveId(selection.collectionId);
    const requestId = parsePositiveId(selection.requestId);
    const environmentId = parsePositiveId(selection.environmentId);
    if (projectId != null) cleaned.projectId = projectId;
    if (collectionId != null) cleaned.collectionId = collectionId;
    if (requestId != null) cleaned.requestId = requestId;
    if (environmentId != null) cleaned.environmentId = environmentId;
    if (selection.projectName?.trim()) cleaned.projectName = selection.projectName.trim();
    sessionStorage.setItem(AGENT_COLLECTIONS_SELECTION_KEY, JSON.stringify(cleaned));
    if (environmentId != null) {
      sessionStorage.setItem('collectionsAgentEnvironmentId', String(environmentId));
    }
  } catch {
    /* ignore */
  }
}

export function readAgentCollectionsSelection(): AgentCollectionsSelection | null {
  try {
    const raw = sessionStorage.getItem(AGENT_COLLECTIONS_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AgentCollectionsSelection;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      projectId: parsePositiveId(parsed.projectId),
      projectName: typeof parsed.projectName === 'string' ? parsed.projectName : undefined,
      collectionId: parsePositiveId(parsed.collectionId),
      requestId: parsePositiveId(parsed.requestId),
      environmentId: parsePositiveId(parsed.environmentId),
    };
  } catch {
    return null;
  }
}

/** Prefer live Collections page selection; fall back to last stored snapshot. */
export function resolveAgentCollectionsSelection(
  timeoutMs = 400,
): Promise<AgentCollectionsSelection | null> {
  if (typeof window === 'undefined') {
    return Promise.resolve(readAgentCollectionsSelection());
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: AgentCollectionsSelection | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      window.removeEventListener(
        'ai-resolve-collections-selection-result',
        onResult as EventListener,
      );
      resolve(value);
    };
    const onResult = (event: Event) => {
      const detail = (event as CustomEvent<AgentCollectionsSelection | null>).detail;
      finish(detail ?? null);
    };
    const timeout = window.setTimeout(() => {
      finish(readAgentCollectionsSelection());
    }, timeoutMs);
    window.addEventListener('ai-resolve-collections-selection-result', onResult as EventListener);
    window.dispatchEvent(new CustomEvent('ai-resolve-collections-selection'));
  });
}
